import React from "react";
import GenericTable from "@surgia/ui/containers/table/generic-table";
import { TableColumn } from "@surgia/ui/containers/table/constants";
import { formatDateWithMoment } from "@surgia/utils";
import InviteUserIcon from "@surgia/ui/icons/users/inviteUser.svg";
import DeleteIcon from "@surgia/ui/icons/actions/delete2.svg";
import TruncatedTooltip from "@surgia/ui/components/truncatedTooltip";

interface IBranchesTableProps {
  data: any;
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  hasRequiredEditPermission: boolean;
  // New sorting props
  sortState?: { column: string | null; direction: "asc" | "desc" | null };
  onSort?: (sortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => void;
  // Action handlers
  onEdit?: (branch: any) => void;
  onDelete?: (branch: any) => void;
  onInviteUser?: (branch: any) => void;
}

const BranchesTable: React.FC<IBranchesTableProps> = ({
  data,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  sortState,
  onSort,
  hasRequiredEditPermission = true,
  onEdit = () => {
    alert("upcoming feature");
  },
  onDelete = () => {
    alert("upcoming feature");
  },
  onInviteUser = () => {
    alert("upcoming feature");
  },
}) => {
  const columns: TableColumn[] = [
    {
      key: "id",
      translationKey: "ID",
      sortable: true,
      sortType: "number",
      sortKey: "id",
      render: (val: any, row: any) => (
        <div className="relative group">
          <button
            onClick={() => onEdit(row)}
            className="cursor-pointer text-purple-700 hover:text-purple-700/80 transition-colors"
          >
            {val}
          </button>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
            {`Edit ${val}`}
          </div>
        </div>
      ),
    },
    {
      key: "name",
      translationKey: "Branch name",
      sortable: true,
      sortType: "string",
      sortKey: "name", // API field name for sorting
      render: (row: any) => (
        <TruncatedTooltip content={row} useTableRowMain={true} as="p" />
      ),
    },
    {
      key: "city",
      translationKey: "City",
      sortable: true,
      sortType: "string",
      sortKey: "address.city.name",
      render: (row: any) => (
        <TruncatedTooltip content={row} useTableRowMain={true} as="p" />
      ),
    },
    {
      key: "area",
      translationKey: "Area",
      sortable: true,
      sortType: "string",
      sortKey: "address.area.name",
      render: (row: any) => (
        <TruncatedTooltip content={row} useTableRowMain={true} as="p" />
      ),
    },
    {
      key: "branch_users_count",
      translationKey: "Users",
      sortable: true,
      sortType: "number",
      sortKey: "usersCount",
    },
    {
      key: "contact_person_full_name",
      translationKey: "Contact name",
      sortable: true,
      sortType: "string",
      sortKey: "contactPersonFullName",
      render: (row: any) => (
        <TruncatedTooltip content={row} useTableRowMain={true} as="p" />
      ),
    },
    {
      key: "contact_person_mobile",
      translationKey: "Contact details",
      sortable: false,
      render: (row: any) => (
        <div className="text-gray-500">
          <TruncatedTooltip
            content={row.mobile}
            textClassName="text-[14px] text-[#181D27]"
          />
          <TruncatedTooltip
            content={row.email}
            textClassName="text-[14px] text-[#181D27]"
          />
        </div>
      ),
    },
    {
      key: "created_at",
      translationKey: "Date created",
      sortable: true,
      sortType: "date",
      sortKey: "createdAt",
      render: (row: any) => formatDateWithMoment(row),
    },
    {
      key: "actions",
      translationKey: "Actions",
      sortable: false,
      render: (row: any) => (
        <div className="flex items-center gap-2 min-w-[80px]">
          {hasRequiredEditPermission && onDelete && (
            <div className="relative group">
              <button
                onClick={() => onDelete(row)}
                className="p-1 rounded transition-colors duration-200 "
              >
                <img src={DeleteIcon} alt="Delete branch" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                Delete branch
              </div>
            </div>
          )}
          {onInviteUser && (
            <div className="relative group">
              <button
                onClick={() => onInviteUser(row)}
                className="p-1 rounded transition-colors duration-200 "
              >
                <img src={InviteUserIcon} alt="Invite user to branch" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                Add user
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <GenericTable
      columns={columns}
      data={data}
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      sortState={sortState}
      onSort={onSort}
      enableInternalSorting={false}
    />
  );
};

export default BranchesTable;
